/**
 * Refreshing an open folder list after a remote change, bounded.
 *
 * The list's cursor is forward-only, so an infinite query refetches its pages
 * one after another, each from the previous page's cursor. Left alone, a
 * change refetches every page the user ever scrolled through (84 requests in
 * sequence for a list scrolled 5,000 rows deep), and each change that arrives
 * mid-walk cancels it and starts again from page 1.
 *
 * Two bounds:
 * - Pages below the last visible row, plus one, are dropped before the
 *   refetch. Rows on screen are never dropped, so the view does not jump; a
 *   user at row 5,000 still pays the walk down to it, which a forward cursor
 *   cannot avoid. A list no view has reported on keeps its first page.
 * - A list already fetching is left to finish and refreshed once more after,
 *   however many changes arrived meanwhile.
 */

import {
  hashKey,
  type InfiniteData,
  type Query,
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query';

interface Page {
  readonly items: readonly unknown[];
}

/** The last row index each list's view has on screen, by query hash. */
const visibleEnd = new Map<string, number>();

/** A list's identity for `noteVisibleEnd`: its query hash. */
export const listHashOf = (queryKey: QueryKey): string => hashKey(queryKey);

/** Called by the list views as their rendered window moves. */
export function noteVisibleEnd(listHash: string, lastIndex: number): void {
  visibleEnd.set(listHash, lastIndex);
}

/** The pages holding rows 0..lastVisible, plus one; one page when unknown. */
function pagesToKeep(pages: readonly Page[], lastVisible: number | undefined): number {
  if (lastVisible === undefined) return 1;
  let rows = 0;
  for (let i = 0; i < pages.length; i++) {
    rows += pages[i].items.length;
    if (rows > lastVisible) return Math.min(pages.length, i + 2);
  }
  return pages.length;
}

function trimAndRefetch(queryClient: QueryClient, query: Query): void {
  const data = query.state.data as InfiniteData<Page> | undefined;
  const keep = data ? pagesToKeep(data.pages, visibleEnd.get(query.queryHash)) : 0;
  if (data && keep < data.pages.length) {
    queryClient.setQueryData<InfiniteData<Page>>(query.queryKey, {
      pages: data.pages.slice(0, keep),
      pageParams: data.pageParams.slice(0, keep),
    });
  }
  void queryClient.invalidateQueries({ queryKey: query.queryKey, exact: true });
}

/** Lists that changed while fetching, refreshed when the fetch settles. */
const deferred = new Set<string>();
const watching = new WeakSet<QueryClient>();

function deferUntilSettled(queryClient: QueryClient, query: Query): void {
  deferred.add(query.queryHash);
  if (watching.has(queryClient)) return;
  watching.add(queryClient);
  queryClient.getQueryCache().subscribe((event) => {
    const { query: settled } = event;
    // A real settle only. A cancel also goes idle (it reverts state), and the
    // one who cancels is about to write optimistically: refetching now would
    // race that write with a request sent before it (a move's onMutate).
    // The deferral waits for the next fetch to settle instead.
    const done =
      event.type === 'updated' &&
      (event.action.type === 'success' || event.action.type === 'error');
    if (!done || !deferred.has(settled.queryHash)) return;
    deferred.delete(settled.queryHash);
    // Out of the cache's notify cycle before writing to it again.
    queueMicrotask(() => trimAndRefetch(queryClient, settled));
  });
}

/** Refresh every folder list the filters match, within the bounds above. */
export function refreshFolderLists(
  queryClient: QueryClient,
  filters: { readonly queryKey: QueryKey; readonly predicate?: (query: Query) => boolean },
): void {
  for (const query of queryClient.getQueryCache().findAll(filters)) {
    if (query.state.fetchStatus === 'fetching') deferUntilSettled(queryClient, query);
    else trimAndRefetch(queryClient, query);
  }
}
